<template>
  <div
    :class="{ 'h-full': $route.meta.explore }"
  >
    <Fullscreen
      ref="fullscreen"
      class="h-full relative"
    >
      <button
        v-if="!$route.meta.explore && !mobile"
        class="absolute top-6 right-6 z-10"
        aria-label="Full screen"
        @click="fullScreen"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" v-if="!fullscreen" v-bind:svg-inline="''" v-bind:class="'h-6 w-6 text-white fill-current'" v-bind:aria-hidden="'true'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M29.414 26.586L22.828 20 20 22.828l6.586 6.586L24 32h8v-8zM2.586 5.414L9.172 12 12 9.172 5.414 2.586 8 0H0v8zm24-2.828L20 9.172 22.828 12l6.586-6.586L32 8V0h-8zM12 22.828L9.172 20l-6.586 6.586L0 24v8h8l-2.586-2.586z"/></svg>
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" v-if="fullscreen" v-bind:svg-inline="''" v-bind:class="'h-6 w-6 text-white fill-current'" v-bind:aria-hidden="'true'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M28 20h-8v8l2.586-2.586L29.172 32 32 29.172l-6.586-6.586zM25.414 9.414L32 2.828 29.172 0l-6.586 6.586L20 4v8h8zM2.828 0L0 2.828l6.586 6.586L4 12h8V4L9.414 6.586zm3.758 22.586L0 29.172 2.828 32l6.586-6.586L12 28v-8H4z"/></svg>
      </button>
      <div
        id="pano"
        class="h-full z-0 relative"
      />
    </Fullscreen>
    <Sidebar
      v-show="$route.meta.sidebar"
      class="z-10"
      @hotspot-click="onSideNavClick"
    />
  </div>
</template>

<script>
/* global removepano: false, embedpano: false*/
import { mapGetters, mapState } from 'vuex'
import Sidebar from '@/components/Sidebar.vue'
import Fullscreen from 'vue-fullscreen/src/component.vue'

export default {
  name: 'Panorama',

  components: {
    Sidebar,
    Fullscreen
  },

  data: () => ({
    pano: '',
    fullscreen: false
  }),

  computed: {
    ...mapGetters([ 'post', 'posts', 'panorama' ]),
    ...mapState([ 'mobile' ]),

    hotspots() {
      return this.panorama.children ? this.panorama.children : []
    }
  },

  watch: {
    $route(from, to) {
      // Check if coming back from a hotspot/media_id route and don't refresh the pan
      if (!to.params.media_id) {
        this.krpready(this.pano)
      }
    }
  },

  mounted() {
    embedpano({
      xml: 'vendor/krpano.xml',
      target: 'pano',
      id: 'rama',
      mobilescale: 1.0,
      onready: this.krpready
    })
  },

  beforeDestroy() {
    removepano('rama')
    this.pano = false
  },

  methods: {
    krpready(rama) {
      window.onSpotClick = this.onSpotClick
      window.onNavSpotClick = this.onNavSpotClick
      window.rvonentervr = this.rvonentervr
      window.rvonexitvr = this.rvonexitvr

      this.pano = rama

      const krpScene = `
        <krpano>
          <view
            fov="80.0"
            fovmax="105"
            fovmin="65"
            hlookat="${this.panorama.position.horizontal}"
            vlookat="${this.panorama.position.vertical}"
            hlookatmin="${this.panorama.corners.west}"
            hlookatmax="${this.panorama.corners.east}"
            vlookatmin="${-90}"
            vlookatmax="${90}"
            limitview="lookat"
            fisheye="0" />
          <preview url="${this.panorama.panorama.sizes.medium_large}?nocache=${Date.now()}"/>
          <image>
            <sphere url="${this.panorama.panorama.url}"/>
          </image>
        </krpano>
      `

      const hotspots = this.hotspots.reduce((crop, item, i) => {
        const textContent = `${item.description ? item.description.substring(0, 60) : item.body.replace(/<\/?[^>]+(>|$)/g, '').substring(0, 60)}...`
        // const escapedTextContent = escape(textContent)
        const hotspot = `
          <div class="hotspot relative bg-white w-56 text-sm">
            <div class="relative pl-2 pr-10 flex items-center bg-grey-300 h-12 border-l-8 border-${item.color}">
              <h5 class="font-medium">${escape(item.title)}</h5>
              <img src="${require(`@/assets/icon-${item.type}.svg`)}" class="h-6 w-6 absolute pin-c-y right-3" />
            </div>
            <div class="p-2 h-14 font-light flex items-center">
              <p>${escape(textContent)}</p>
            </div>
          </div>
          `
        const hotspotId = `${item.slug}-${i}`

        return `
          ${crop}
          addhotspot(${hotspotId});
          set(hotspot[${hotspotId}].ath, ${item.horizontal});
          set(hotspot[${hotspotId}].atv, ${item.vertical});
          set(hotspot[${hotspotId}].type, text);
          set(hotspot[${hotspotId}].html, '${hotspot}');
          set(hotspot[${hotspotId}].padding, 0);
          set(hotspot[${hotspotId}].wordwrap, false);
          set(hotspot[${hotspotId}].interactivecontent, true);
          set(hotspot[${hotspotId}].zoom, false);
          set(hotspot[${hotspotId}].bg, false);
          set(hotspot[${hotspotId}].renderer, css3d);
          set(hotspot[${hotspotId}].onclick, looktohotspot(${hotspotId}, 50); js(onSpotClick(${hotspotId})));
        `
      }, '')

      const krpCall = this.$route.path.includes('explore')
        ? `loadxml(${krpScene}, null, MERGE, blend(1, linear)); ${hotspots}`
        : `loadxml(${krpScene}, null, MERGE, blend(1, linear));`

      this.pano.call(krpCall)
    },
    onSpotClick(hotspotId) {
      const hotspotTarget = hotspotId.split('-').pop()
      this.$router.push(`${this.$route.path}/${this.hotspots[hotspotTarget].slug}`)
    },
    onSideNavClick(index) {
      // // Add 2 to index because webvr prev next spots and cursor are index 0 and 1 and 2
      this.pano.call(`looktohotspot(${index + 2}, 50);`)
      // this.pano.call(`looktohotspot(${index}, 50);`)
      // Set timeout on route change to allow looking to hotspot first
      // setTimeout(() => {
      //   this.$router.push(`${this.$route.path}/${this.hotspots[index].slug}`)
      // }, 1000)
    },
    toggleGyro() {
      this.pano.call('switch(plugin[gyro].enabled)')
    },
    enterVr() {
      this.pano.call('webvr.toggleVR()')
    },
    rvonentervr() {
      this.pano.call('set(events[vr_events].onviewchange, changeEvents()); set(hotspot[followMenuPrev].visible, true); set(hotspot[followMenuNext].visible, true)')
    },
    rvonexitvr() {
      this.pano.call('set(events[vr_events].onviewchange, null); set(hotspot[followMenuPrev].visible, false); set(hotspot[followMenuNext].visible, false)')
    },
    rvvrnext() {

    },
    rvvrprevious() {

    },
    fullScreen() {
      this.$refs.fullscreen.toggle()
      this.fullscreen = !this.fullscreen
    }
  }
}
</script>

<style>
  .hotspot {
    white-space: normal;
  }

  .hotspot:after {
    content:'';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -10px;
    width: 0;
    height: 0;
    border-top: solid 10px #fff;
    border-left: solid 10px transparent;
    border-right: solid 10px transparent;
  }
</style>
